import React, { useContext, useMemo } from 'react';
import { Container, Grid } from '@mui/material';
import Api from '../../api';
import { useLoaderData } from 'react-router-dom';
import { motion } from 'framer-motion';
import _ from 'lodash';

import { Calender } from './shared/calender-widget/calender';
import ShortCut from './shared/shortcuts/Shortcut';
import Weather from './shared/weather/Weather';
import ITSupport from './shared/itsupport/ITSupport';
import Clock from './shared/clock/Clock';
import { componentList } from './component_list';
import style from './Home.module.css';
import UserContext from '../../context/userContext';
import { getLabel } from '../../utils';

export async function homeLoader() {
    return Api.get('/intranet/home');
}

interface IHomeComponent {
    key: string;
    data: any;
}

export interface IShortcutLink {
    title: string;
    url: string;
    image: string | null;
}

export interface IShortcut {
    name: string;
    link: [IShortcutLink];
}

interface IHomeProps {
    components: [IHomeComponent];
    shortcuts: [IShortcut];
}

function greetingKey(hour: number): string {
    if (hour < 5) return 'home.greetingNight';
    if (hour < 10) return 'home.greetingMorning';
    if (hour < 13) return 'home.greetingDay';
    if (hour < 18) return 'home.greetingAfternoon';
    if (hour < 23) return 'home.greetingEvening';
    return 'home.greetingNight';
}

const cardVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: 0.06 + i * 0.04, duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
    }),
};

function MotionCard({ index, children, className }: { index: number; children: React.ReactNode; className?: string }) {
    return (
        <motion.div
            className={`${style.cardStyle} ${className ?? ''}`}
            custom={index}
            initial="hidden"
            animate="visible"
            variants={cardVariants}
            whileHover={{ y: -2 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        >
            {children}
        </motion.div>
    );
}

function DynamicComponents() {
    const data: IHomeProps = useLoaderData() as IHomeProps;
    const components: [IHomeComponent] = data.components;
    return (
        <>
            {components?.map((component, index) => {
                const Component = componentList.find(c => c.key === component.key);
                if (!Component) {
                    console.error('Component not found', component);
                    return null;
                }
                const propsData = _.get(component, 'props.data', null);
                if (!propsData) return null;
                if (Array.isArray(propsData) && propsData.length === 0) return null;
                return (
                    <MotionCard key={index} index={index + 2}>
                        <Component.comp {..._.get(component, 'props', {})} />
                    </MotionCard>
                );
            })}
        </>
    );
}

function Home() {
    const data: IHomeProps = useLoaderData() as IHomeProps;
    const { userData } = useContext(UserContext);
    const lang: string = userData?.user?.lang ?? 'no';
    const firstName: string = _.get(userData, 'user.firstname', '');

    const greeting = useMemo(
        () => getLabel(greetingKey(new Date().getHours()), lang),
        [lang],
    );

    return (
        <>
            <header className={style.hero}>
                <Container maxWidth={false} className={style.heroInner}>
                    <motion.h1
                        className={style.heroGreeting}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
                    >
                        <span className={style.greetingItalic}>{greeting}</span>
                        {firstName && (
                            <>
                                <span className={style.greetingComma}>,&thinsp;</span>
                                <span className={style.greetingName}>{firstName}.</span>
                            </>
                        )}
                    </motion.h1>

                </Container>
            </header>

            <Container maxWidth={false} className={style.boardWrap}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <MotionCard index={0} className={style.clockCard}>
                            <Clock />
                        </MotionCard>
                        <MotionCard index={1} className={style.weatherCard}>
                            <Weather />
                        </MotionCard>
                        <MotionCard index={2} className={style.calCard}>
                            <Calender />
                        </MotionCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DynamicComponents />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <MotionCard index={3} className={style.itSupportCard}>
                            <ITSupport />
                        </MotionCard>
                        {data?.shortcuts?.map((shortcut, index) => (
                            <MotionCard key={index} index={index + 4} className={style.shortcutCard}>
                                <ShortCut {...shortcut} />
                            </MotionCard>
                        ))}
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}

export default Home;
